import React, { useEffect, useMemo, useRef, useState } from "react";
import { SxProps } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { useGetGeoRegionsQuery } from "../../../store/api/admin/adminApiSlice";
import { IGeoRegion } from "../../../store/api/admin/types";
import { cn } from "../../../lib/utils";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Checkbox } from "../../ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import { Icon } from "../Icon";

interface GeoRegionFilterProps {
  selectedRegionIds: string[];
  onSelectionChange: (regionIds: string[]) => void;
  regions?: IGeoRegion[];
  selectSx?: SxProps<Theme>;
  menuPaperSx?: SxProps<Theme>;
}

const GeoRegionFilter: React.FC<GeoRegionFilterProps> = ({
  selectedRegionIds,
  onSelectionChange,
  regions,
}) => {
  const { data: geoRegionsData } = useGetGeoRegionsQuery(undefined, { skip: !!regions });
  const geoRegions = regions || geoRegionsData || [];

  const sortedRegionOptions = useMemo(() => {
    if (!geoRegions || geoRegions.length === 0) return [] as IGeoRegion[];
    return [...geoRegions]
      .filter((region) => region?.name && region?.objectKey)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [geoRegions]);

  const allKeys = useMemo(
    () => sortedRegionOptions.map((r) => r.objectKey),
    [sortedRegionOptions],
  );

  const isAllSelected =
    sortedRegionOptions.length > 0 &&
    selectedRegionIds.length === sortedRegionOptions.length;

  // By default, select all available regions once on initial load
  const hasInitialized = useRef(false);

  // Pending selection state while the dropdown is open
  const [isOpen, setIsOpen] = useState(false);
  const [pendingSelection, setPendingSelectionState] = useState<string[]>([]);
  const pendingSelectionRef = useRef<string[]>([]);
  const selectionSnapshot = useRef<string[]>([]);

  const setPendingSelection = (val: string[]) => {
    pendingSelectionRef.current = val;
    setPendingSelectionState(val);
  };

  useEffect(() => {
    if (hasInitialized.current) return;
    if (sortedRegionOptions.length > 0 && (!selectedRegionIds || selectedRegionIds.length === 0)) {
      onSelectionChange(allKeys);
      hasInitialized.current = true;
    }
  }, [sortedRegionOptions, selectedRegionIds, onSelectionChange, allKeys]);

  // Reconcile selectedRegionIds when available regions change (e.g., after deletion)
  useEffect(() => {
    if (!selectedRegionIds || selectedRegionIds.length === 0) return;
    const availableKeys = new Set(allKeys);
    const validIds = selectedRegionIds.filter((id) => availableKeys.has(id));

    // If some selected regions no longer exist, update the selection
    if (validIds.length !== selectedRegionIds.length) {
      // If all valid regions match all available regions, show "All Regions"
      if (validIds.length === allKeys.length && allKeys.length > 0) {
        onSelectionChange(allKeys);
      } else if (validIds.length > 0) {
        // Otherwise, keep only the valid ones
        onSelectionChange(validIds);
      } else if (allKeys.length > 0) {
        // If no valid regions remain, default to all
        onSelectionChange(allKeys);
      }
    }
  }, [allKeys, selectedRegionIds, onSelectionChange]);

  // Snapshot + commit logic on open/close
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      // Capture selection at the moment the dropdown opens
      selectionSnapshot.current = selectedRegionIds;
      setPendingSelection(selectedRegionIds);
    } else {
      // Dropdown closing: if user left everything unchecked, restore the snapshot
      if (pendingSelectionRef.current.length === 0) {
        onSelectionChange(selectionSnapshot.current);
      } else {
        onSelectionChange(pendingSelectionRef.current);
      }
    }
  };

  // While the dropdown is open, checkboxes reflect pending state; otherwise committed state
  const effectiveSelection = isOpen ? pendingSelection : selectedRegionIds;
  const currentlyAllSelected =
    sortedRegionOptions.length > 0 && effectiveSelection.length === sortedRegionOptions.length;

  const toggleRegion = (objectKey: string | null) => {
    if (objectKey === null) {
      // "All Regions" toggled
      if (currentlyAllSelected) {
        // Uncheck all — allow empty pending state; close handler will restore if needed
        setPendingSelection([]);
      } else {
        setPendingSelection(allKeys);
      }
      return;
    }

    const has = effectiveSelection.includes(objectKey);
    if (has) {
      // Allow deselecting down to zero while the dropdown is open
      setPendingSelection(effectiveSelection.filter((r) => r !== objectKey));
    } else {
      setPendingSelection([...effectiveSelection, objectKey]);
    }
  };

  // Build trigger label using region names (matching original renderRegionValue)
  const triggerLabel = useMemo(() => {
    if (!sortedRegionOptions.length || selectedRegionIds.length === 0) return "All Regions";
    if (isAllSelected) return "All Regions";

    const firstRegion = sortedRegionOptions.find(
      (r) => r.objectKey === selectedRegionIds[0],
    );
    if (selectedRegionIds.length === 1) return firstRegion?.name || "-";
    return `${firstRegion?.name || "-"} +${selectedRegionIds.length - 1}`;
  }, [selectedRegionIds, sortedRegionOptions, isAllSelected]);

  return (
    <DropdownMenu onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          title={triggerLabel}
          className={cn(
            "group w-[160px] justify-between gap-2 text-primary-foreground shadow-none [&>svg]:shrink-0 [&>svg]:text-primary-foreground",
            "hover:text-white hover:[&>svg]:text-white")}
          style={{
            border: '1px solid rgba(255, 255, 255, 0.3)',
            backgroundColor: 'transparent',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
          }}
        >
          <span className="min-w-0 truncate text-left">{triggerLabel}</span>
          <div className="flex shrink-0 items-center gap-1">
            {!isAllSelected && selectedRegionIds.length > 1 && (
              <Badge variant="secondary" className="flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px] font-semibold tabular-nums bg-primary-foreground/20 text-primary-foreground border-0 group-hover:bg-white/30 group-hover:text-white">
                {selectedRegionIds.length}
              </Badge>
            )}
            <Icon name="expand_more" size={18} />
          </div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={4} className="z-[9999] w-[240px] p-0 bg-popover text-popover-foreground" onCloseAutoFocus={(e) => e.preventDefault()}>
        <DropdownMenuLabel className="pl-4 text-foreground">Regions</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {/* "All Regions" item */}
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            toggleRegion(null);
          }}
          className="cursor-pointer focus:bg-accent hover:bg-accent py-0 pl-2 mt-0 !pt-0"
          style={{ height: "30px" }}
        >
          <label className="flex cursor-pointer items-center gap-2 w-full pointer-events-none">
            <Checkbox
              checked={currentlyAllSelected}
              aria-label="Select All Regions"
              tabIndex={-1}
              className="pointer-events-none"
            />
            <span>All Regions</span>
          </label>
        </DropdownMenuItem>
        {/* Individual region items */}
        {sortedRegionOptions.map((region) => {
          const checked = effectiveSelection.includes(region.objectKey);
          return (
            <DropdownMenuItem
              key={region.objectKey}
              onSelect={(e) => {
                e.preventDefault();
                toggleRegion(region.objectKey);
              }}
              className="cursor-pointer focus:bg-accent hover:bg-accent py-0 pl-2 mt-0 !pt-0"
              style={{ height: "30px" }}
            >
              <label className="flex cursor-pointer items-center gap-2 w-full pointer-events-none">
                <Checkbox
                  checked={checked}
                  aria-label={`Select ${region.name}`}
                  tabIndex={-1}
                  className="pointer-events-none"
                />
                <span>{region.name}</span>
              </label>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default GeoRegionFilter;